import React, { useState, useEffect } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import { Button } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { FaTable, FaListUl, FaEdit, FaArrowLeft } from "react-icons/fa";
import { HiPencilAlt } from "react-icons/hi";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Pagination } from "@mui/material";
import Loader from "../../Helper/Loader";
import config from "../../../config";
import ReviewModal from "./ReviewModal";
import Breadcrumbs from "../../Helper/Breadcrumbs";
import CustomSearch from "../../Helper/CustomSearch";

const icon = `${process.env.PUBLIC_URL}/img/images/order-images.jpg`;

const EventApprovalIndex = () => {
    const history = useHistory();
    const location = useLocation();
    const [data, setData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVendorId, setSelectedVendorId] = useState(null);
    const [view, setView] = useState("list");
    // Default type based on URL
    const [approvalType, setApprovalType] = useState(
        location.pathname.includes("pendingvendor-list") ? "PENDING" : "APPROVED"
    );

    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState({
        page: 1,
        count: 1,
        total: 0,
    });

    const tokenKey = {
        headers: { Authorization: "Token " + sessionStorage.getItem("tokenKey") },
    };

    // Effect to update validation type if URL changes (back/forward browser nav)
    useEffect(() => {
        setApprovalType(location.pathname.includes("pendingvendor-list") ? "PENDING" : "APPROVED");
    }, [location.pathname]);

    const filterData = (allData, type, term) => {
        let filtered = allData;

        // Filter by Approval Type
        if (type === "APPROVED") {
            // For APPROVED view, we trust the API or include all "Approved"-like statuses
            // We do NOT filter by status strictly if the API handles it, 
            // but if we need compatibility, we ensure we don't accidentally hide items.
            // Since we are switching APIs, we'll assume the data is correct. 
            // However, to be safe, we can filter to be sure if using mixed data (legacy safety),
            // but relying on the API is better. We'll Just pass through or perform minimal checks.
            // filtered = filtered.filter(item => item.status === "APPROVED"); <- OLD
            // New: Don't status filter, or allow ACTIVE/MIGRATED
        } else {
            // Pending means NOT Approved, NOT Migrated, NOT Active
            filtered = filtered.filter(item => {
                const s = (item.status || "").toUpperCase();
                return s !== "APPROVED" && s !== "MIGRATED" && s !== "MIGRATED" && s !== "ACTIVE";
            });
        }

        // Filter by Search Term
        if (term) {
            const lower = term.toLowerCase();
            filtered = filtered.filter(item =>
                (item.business_name && item.business_name.toLowerCase().includes(lower)) ||
                (item.phone_number && item.phone_number.includes(term)) ||
                (item.category && item.category.toLowerCase().includes(lower)) ||
                (item.primary_city && item.primary_city.toLowerCase().includes(lower)) ||
                (item.operating_cities && item.operating_cities.some(c => c.name.toLowerCase().includes(lower)))
            );
        }
        return filtered;
    };

    const manipulateData = (results) => {
        // Normalization: Map operating_cities to primary_city if primary_city is missing
        const normalizedResults = results.map(item => {
            if (!item.primary_city && item.operating_cities && item.operating_cities.length > 0) {
                // Use the first city as primary for display if missing
                return { ...item, primary_city: item.operating_cities[0].name };
            }
            return item;
        });

        setData(normalizedResults); // Store full raw data

        // Filter data using current state
        const filtered = filterData(normalizedResults, approvalType, searchTerm);
        setTableData(filtered);

        setPage({
            page: 1,
            count: Math.ceil(filtered.length / 10),
            total: filtered.length
        });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            let url;
            if (approvalType === "APPROVED") {
                // Use new API for Approved list
                // User text: GET /eventvendors/admin/profiles/
                // Assumed standard v2 prefix and hyphenated event-vendors based on project patterns
                url = `${config.service}/v2/event-vendors/admin/profiles/`;
            } else {
                // Use existing list API for Pending
                url = `${config.service}/v2/event-vendors/admin/list/`;
            }

            const res = await axios.get(url, tokenKey);
            if (res.data) {
                let results = [];
                if (approvalType === "APPROVED") {
                    // Handle { profiles: [...] }
                    results = res.data.profiles || [];
                    // Ensure status is displayable if missing
                    results = results.map(r => ({ ...r, status: r.status || 'APPROVED' }));
                } else {
                    // Handle array or { results: [] }
                    results = Array.isArray(res.data) ? res.data : (res.data.results || []);
                }
                manipulateData(results);
            } else {
                setData([]);
                setTableData([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setData([]);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [approvalType]); // Refetch when approvalType changes (route change)

    // Re-filter when approvalType (URL changes) or search or data changes
    useEffect(() => {
        if (data.length > 0) {
            const filtered = filterData(data, approvalType, searchTerm);
            setTableData(filtered);
            setPage({
                page: 1,
                count: Math.ceil(filtered.length / 10),
                total: filtered.length
            });
        }
    }, [approvalType, searchTerm, data]);

    const handleAction = (id) => {
        setSelectedVendorId(id);
        setModalOpen(true);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const navigateToPending = () => {
        history.push("/pendingvendor-list");
    };

    const navigateToApproved = () => {
        history.push("/event-approval");
    };

    const columns = [
        {
            dataField: "id",
            text: "ID",
            headerStyle: { width: "60px" },
        },
        {
            dataField: "business_name",
            text: "Business Name",
            formatter: (cell, row) => cell || row.phone_number || "N/A"
        },
        {
            dataField: "category",
            text: "Category",
            formatter: (cell) => cell || "N/A"
        },
        {
            dataField: "primary_city",
            text: "City",
            formatter: (cell) => cell || "N/A"
        },
        {
            dataField: "status",
            text: "Status",
            formatter: (cell) => {
                const isApproved = cell === 'APPROVED' || cell === 'MIGRATED' || cell === 'Migrated';
                const displayStatus = (cell === 'MIGRATED' || cell === 'Migrated') ? 'APPROVED' : cell;
                const badgeClass = isApproved ? 'success' : cell === 'DRAFT' ? 'secondary' : 'warning';

                return (
                    <span className={`badge badge-${badgeClass}`}>
                        {displayStatus}
                    </span>
                );
            }
        },
        {
            dataField: "actions",
            text: "Actions",
            formatter: (cell, row) => (
                <div className="tableActions">
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleAction(row.id)}
                    >
                        Review
                    </Button>
                </div>
            ),
        },
    ];

    const paginationOptions = paginationFactory({
        sizePerPage: 10,
        hideSizePerPage: true,
        hidePageListOnlyOnePage: true,
    });

    // Dynamic Breadcrumb
    const breadcrumbData = [
        { title: "Vendor", href: "#" },
        { title: approvalType === "APPROVED" ? "Event Approval" : "Pending Approvals", href: "#" }
    ];

    return (
        <Container className="vendor">
            <Row>
                <Col md={12} className="contBody">
                    <Card>
                        <CardBody>
                            <div className="card__title pageNav">
                                <div className="bold-text">
                                    <Breadcrumbs data={breadcrumbData} />
                                </div>
                            </div>

                            <Row className="formBtnsDiv mar-b-10">
                                <Col md={8}>
                                    {approvalType === "APPROVED" ? (
                                        <Button
                                            variant="primary"
                                            onClick={navigateToPending}
                                        >
                                            Pending Approvals
                                        </Button>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <Button
                                                variant="outline-primary"
                                                onClick={navigateToApproved}
                                            >
                                                <FaArrowLeft style={{ marginRight: "5px" }} /> Back to Approved List
                                            </Button>
                                        </div>
                                    )}
                                </Col>
                                <Col md={4}>
                                    <CustomSearch callbackFn={handleSearch} />
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <div className="viewIcons">
                                        <span>
                                            <FaListUl
                                                title="Card View"
                                                onClick={() => setView("list")}
                                                style={{ color: view === 'list' ? '#70bbfd' : 'inherit' }}
                                            />
                                        </span>
                                        <span>
                                            <FaTable
                                                title="List View"
                                                onClick={() => setView("table")}
                                                style={{ color: view === 'table' ? '#70bbfd' : 'inherit' }}
                                            />
                                        </span>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="vendorList">
                                <Loader loading={loading}>
                                    {view === "list" ? (
                                        tableData.length > 0 ? (
                                            tableData.map((row) => {
                                                let coverImg = row.cover_image || icon;
                                                const isRowApproved = row.status === 'APPROVED' || row.status === 'MIGRATED' || row.status === 'Migrated';
                                                const rowDisplayStatus = (row.status === 'MIGRATED' || row.status === 'Migrated') ? 'APPROVED' : row.status;

                                                return (
                                                    <Col md={5} className="divCont" style={{ display: "block" }} key={row.id}>
                                                        <Row>
                                                            <Col md={5}>
                                                                <div className="child" style={{ margin: "15px 0" }}>
                                                                    <img
                                                                        className="mandapImage"
                                                                        src={coverImg}
                                                                        alt="vendor"
                                                                        onError={(e) => { e.target.src = icon }}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col md={5}>
                                                                <div className="child">
                                                                    <div className="mandapHeader">
                                                                        <span>{row.business_name || row.phone_number || "No Name"}</span>
                                                                    </div>
                                                                    <div>Category : {row.category}</div>
                                                                    <div>City : {row.primary_city}</div>
                                                                    <div>Mobile : {row.phone_number}</div>
                                                                    <div>
                                                                        Status :{" "}
                                                                        <span className={`badge badge-${isRowApproved ? 'success' : row.status === 'DRAFT' ? 'secondary' : 'warning'}`}>
                                                                            {rowDisplayStatus}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            <Col md={2}>
                                                                <div className="child" style={{ margin: "15px 0" }}>
                                                                    <HiPencilAlt
                                                                        className="color-green actionIcon"
                                                                        title="Review"
                                                                        onClick={() => handleAction(row.id)}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Col>
                                                );
                                            })
                                        ) : (
                                            <div className="textCenter">No data found</div>
                                        )
                                    ) : (
                                        <BootstrapTable
                                            keyField="id"
                                            data={tableData}
                                            columns={columns}
                                            bootstrap4
                                            wrapperClasses="table-responsive"
                                            pagination={paginationOptions}
                                            noDataIndication="No pending approvals found"
                                        />
                                    )}
                                </Loader>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {selectedVendorId && (
                <ReviewModal
                    isOpen={modalOpen}
                    handleClose={() => {
                        setModalOpen(false);
                        setSelectedVendorId(null);
                    }}
                    vendorId={selectedVendorId}
                    onSuccess={fetchData}
                />
            )}
            <ToastContainer autoClose={3000} />
        </Container>
    );
};

export default EventApprovalIndex;
